<script setup lang="ts">
import { GpDropdown, GpTextInput } from '@gportal/uikit';
import useBillingProfileCountries from '@panel/components/user/billing-profiles/composables/useBillingProfileCountries';
import type { BillingProfileModel } from '@panel/components/user/billing-profiles/composables/useBillingProfileFields';
import { onMounted, reactive, watch } from 'vue';

type DropdownObjectType = {
  key: string;
  label: string;
};

const props = defineProps<{
  form: BillingProfileModel;
  supportsVatId: boolean;
  errors: Partial<Record<keyof BillingProfileModel, string>>;
}>();

const emit = defineEmits<{
  (
    e: 'updated:form',
    payload: { field: keyof BillingProfileModel; value: string | DropdownObjectType },
  ): void;
}>();

const { countryOptions, reloadCountries } = useBillingProfileCountries();

const innerForm = reactive<BillingProfileModel>({
  ...props.form,
});

watch(
  () => props.form,
  () => {
    Object.keys(props.form).forEach((key) => {
      innerForm[key] = props.form[key];
    });
  },
  { deep: true },
);

const updateValue = (field: keyof BillingProfileModel, value: string | DropdownObjectType) => {
  if (field === 'country') {
    innerForm[field] = value as DropdownObjectType;
  } else {
    innerForm[field] = value as string;
  }

  emit('updated:form', { field, value });
};

onMounted(() => {
  if (countryOptions.value.length === 0) {
    reloadCountries();
  }
});
</script>

<template>
  <GpTextInput
    class="tw-mb-2"
    label="Company"
    :model-value="innerForm.company"
    name="company"
    input-id="billing-address-company"
    transparent
    :error="(errors as Record<keyof BillingProfileModel, string>).company"
    @update:model-value="(value) => updateValue('company', value)"
  />
  <GpTextInput
    v-if="supportsVatId"
    class="tw-mb-2"
    label="Vat ID"
    :model-value="innerForm.vatId"
    name="vatId"
    input-id="billing-address-vatid"
    transparent
    :error="(errors as Record<keyof BillingProfileModel, string>).vatId"
    @update:model-value="(value) => updateValue('vatId', value)"
  />
  <GpTextInput
    class="tw-mb-2"
    label="Name"
    :model-value="innerForm.name"
    name="name"
    input-id="billing-address-name"
    transparent
    :error="(errors as Record<keyof BillingProfileModel, string>).name"
    @update:model-value="(value) => updateValue('name', value)"
  />
  <GpTextInput
    class="tw-mb-2"
    label="Bill notification e-mail"
    :model-value="innerForm.email"
    name="e-mail"
    input-id="billing-address-email"
    transparent
    @update:model-value="(value) => updateValue('email', value)"
  />
  <GpTextInput
    class="tw-mb-2"
    label="Street"
    :model-value="innerForm.street"
    name="street"
    input-id="billing-address-street"
    transparent
    :error="(errors as Record<keyof BillingProfileModel, string>).street"
    @update:model-value="(value) => updateValue('street', value)"
  />
  <GpTextInput
    class="tw-mb-2"
    label="Postcode"
    :model-value="innerForm.postcode"
    name="postcode"
    input-id="billing-address-postcode"
    transparent
    :error="(errors as Record<keyof BillingProfileModel, string>).postcode"
    @update:model-value="(value) => updateValue('postcode', value)"
  />
  <GpTextInput
    class="tw-mb-2"
    label="City"
    :model-value="innerForm.city"
    name="city"
    input-id="billing-address-city"
    transparent
    :error="(errors as Record<keyof BillingProfileModel, string>).city"
    @update:model-value="(value) => updateValue('city', value)"
  />
  <GpTextInput
    :disabled="innerForm.country.key !== 'US'"
    class="tw-mb-2"
    label="State"
    :model-value="innerForm.state"
    name="state"
    input-id="billing-address-state"
    transparent
    :error="(errors as Record<keyof BillingProfileModel, string>).state"
    @update:model-value="(value) => updateValue('state', value)"
  />
  <GpDropdown
    :model-value="innerForm.country"
    :data="countryOptions"
    input-id="billing-addressress-country"
    label="Country"
    name="country"
    filter
    :emit-object="true"
    @update:model-value="(value) => updateValue('country', value as any)"
  />
</template>

<style scoped></style>
